import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Code, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WidgetPage() {
  const navigate = useNavigate();
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const storeId = localStorage.getItem('store_id');

  useEffect(() => {
    if (!storeId) {
      navigate('/');
      return;
    }
    fetchEmbedCode();
  }, [storeId, navigate]);

  const fetchEmbedCode = async () => {
    try {
      const response = await axios.get(`${API}/widget/embed/${storeId}`);
      setEmbedCode(response.data.embed_code);
    } catch (error) {
      console.error('Error fetching embed code:', error);
      toast.error('Error al obtener código de embed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success('Código copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]" data-testid="widget-page">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="gap-2 mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-zinc-900">Widget Embebible</h1>
          <p className="text-zinc-600 mt-2">Instala el visor 360° en tu tienda de Tiendanube</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Step 1: Copy Code */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">1</div>
              Copia el código del widget
            </CardTitle>
            <CardDescription>
              Este script mostrará automáticamente el visor 360° en las páginas de producto que hayas configurado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <pre className="bg-zinc-900 text-zinc-100 p-4 rounded-lg overflow-x-auto text-sm">
                <code>{loading ? 'Cargando...' : embedCode}</code>
              </pre>
              <Button
                onClick={copyToClipboard}
                size="sm"
                className="absolute top-2 right-2 gap-2"
                variant="secondary"
                disabled={loading}
                data-testid="copy-code-button"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copiado
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copiar
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Step 2: Install in Tiendanube */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">2</div>
              Instala el código en Tiendanube
            </CardTitle>
            <CardDescription>
              Tienes dos opciones para instalar el widget en tu tienda.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Option A */}
            <div className="border border-zinc-200 rounded-lg p-4">
              <h4 className="font-semibold text-zinc-900 mb-2 flex items-center gap-2">
                <Code className="w-5 h-5 text-primary" />
                Opción A: Desde el Panel de Partners (Recomendado)
              </h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700 ml-4">
                <li>Ve a <strong>partners.tiendanube.com</strong> → Tu aplicación</li>
                <li>Busca la sección <strong>"Scripts"</strong></li>
                <li>Haz clic en <strong>"Crear script"</strong></li>
                <li>Selecciona <strong>"Página de producto"</strong> como ubicación</li>
                <li>Pega el código del widget</li>
                <li>Guarda los cambios</li>
              </ol>
              <Alert className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Nota:</strong> Puede que necesites activar el permiso "Write Scripts" en tu panel de Partners primero.
                </AlertDescription>
              </Alert>
            </div>

            {/* Option B */}
            <div className="border border-zinc-200 rounded-lg p-4">
              <h4 className="font-semibold text-zinc-900 mb-2 flex items-center gap-2">
                <Code className="w-5 h-5 text-primary" />
                Opción B: Desde el Administrador de tu Tienda
              </h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700 ml-4">
                <li>Accede al administrador de tu tienda</li>
                <li>Ve a <strong>Mi Tiendanube → Diseño → Editar código</strong></li>
                <li>Abre el archivo <strong>product.html</strong> o <strong>layout.html</strong></li>
                <li>Pega el código justo antes de <code className="bg-zinc-100 px-1 rounded">&lt;/body&gt;</code></li>
                <li>Guarda los cambios</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Step 3: Verify */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">3</div>
              Verifica la instalación
            </CardTitle>
            <CardDescription>
              Comprueba que el widget funciona correctamente en tu tienda.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2 text-sm text-zinc-700">
              <li>Visita una página de producto que tenga configurado el spin 360°</li>
              <li>Deberías ver el visor 360° debajo de la imagen principal del producto</li>
              <li>Prueba a girar el producto arrastrando con el mouse</li>
            </ol>
            
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">Producto configurado:</h4>
              <p className="text-sm text-green-800">
                <strong>Joya corazon</strong> (ID: 326987716) - Tiene spin 360° configurado
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Help */}
        <Card>
          <CardHeader>
            <CardTitle>¿Necesitas ayuda?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-zinc-600 mb-4">
              Si tienes problemas instalando el widget, puedes:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm text-zinc-700">
              <li>Revisar la consola del navegador (F12) para ver posibles errores</li>
              <li>Verificar que el producto tenga configurado un spin 360°</li>
              <li>Asegurarte de que el código esté ubicado correctamente en tu tema</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
